/**
 * QualityWorkspacePage — the Phase 2 split workspace (kanban 12584b08).
 *
 * Every queue row and case card on the door lands here, at a REAL URL:
 *   /portal/quality-copilot/threads/QT-10007
 *   /portal/quality-copilot/cases/INV-00007
 * Detail on the left (Merlin's read, evidence with NEW markers, the decision
 * bar; the case checklist for INV cases), the item's scoped Merlin
 * conversation on the right with the full composer. Both halves render
 * inside the shell, so the tab bar and the portal navbar stay put and the
 * browser's back button returns to the tab you came from — the retired
 * full-viewport board overlay did neither.
 *
 * A case opened from a thread IS that thread's workspace (the case file sits
 * on the thread's detail pane); a case saved straight from chat shows the
 * case file on the left and its originating conversation on the right.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { CaseFileModal } from '../../components/portal/CaseFileModal';
import { useCaseFilePopout } from '../../components/portal/caseFileExpand';
import {
  useQcpDataRequests,
  type QcpDataRequestsState,
} from '../../components/portal/useQcpDataRequests';
import {
  isRoutedToUser,
  markThreadSeen,
  seenSnapshot,
} from '../../components/portal/threadTriageView';
import { AskedOnCasePanel } from '../../components/quality/AskedOnCasePanel';
import { splitAsks } from '../../components/quality/asksView';
import {
  recordThreadView,
  type DoorCaseTaskRead,
  type DoorInvestigationRead,
  type DoorThreadRead,
  type DoorThreadSeenRead,
} from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import { CaseAsksHost, CaseFileSection } from './CaseFileSection';
import { ConversationPane } from './ConversationPane';
import { TaskAskModal } from './TaskAskModal';
import { ThreadDetailPane } from './ThreadDetailPane';
import { ItemNav } from './ItemNav';
import { itemPathFromWorkspace, itemStep, orderFromState, stepActionName, type StepDirection } from './itemNav';
import { useQualityTriage } from './qualityTriageContext';
import {
  ORIGIN_LABEL,
  ORIGIN_PATH,
  asksForCase,
  originFromState,
  searchFromState,
  asksForThread,
  caseForThread,
  resolveCase,
  resolveThread,
  threadForCase,
  threadOrigin,
  threadWorkspacePath,
  type WorkspaceOrigin,
} from './qualityWorkspaceView';
import { railHasRows } from './askRailView';
import { followupsForThread } from './pendingView';
import { handOffRow } from './handOffView';
import { primaryRef } from './caseIdentity';
import { useRecorderObject } from '../../hooks/useRecorderObject';
import styles from './QualityWorkspace.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

/** The door's asks: the shell's shared poll, so reading an answer here
 *  clears the tab badge and the Triage notice at once; a poll of its own
 *  only where no shell provides one (older test harnesses). */
function useDoorAsks(): QcpDataRequestsState {
  const { dataRequests } = useQualityTriage();
  const { user } = useAuth();
  const own = useQcpDataRequests(!dataRequests && !!user?.id);
  return dataRequests ?? own;
}

type NarrowView = 'detail' | 'chat';

const noop = () => undefined;

/** Closing or cancelling a case withdraws its open asks on the server; refetch
 *  when the stage changes so the asks list stops showing them as OPEN until
 *  the next 30s poll. Skips the first render (the hook already fetched). */
function useRefreshAsksOnStageChange(status: string | null, refresh: () => void) {
  const last = useRef(status);
  useEffect(() => {
    if (last.current === status) return;
    last.current = status;
    refresh();
  }, [status, refresh]);
}

function BackBar({
  origin,
  crumb,
  view,
  onView,
  itemNos,
}: {
  origin: WorkspaceOrigin;
  crumb: string;
  view: NarrowView;
  onView: (view: NarrowView) => void;
  /** This item's numbers (a case opened as its thread has two). */
  itemNos: string[];
}) {
  const navigate = useNavigate();
  const state = useLocation().state;
  const order = orderFromState(state);
  const step = itemStep(order, itemNos);
  // Back to the list as it was filtered (waiting filter round).
  const search = searchFromState(state);
  // A step is its own action (qcp_prev / qcp_next, kanban aa0d3fb6): the
  // next item's URL alone looks like a reload of it, so the usage graph
  // could not tell the buttons from a trip back through the list.
  const go = (no: string, direction: StepDirection) => {
    if (step && order) {
      trackEvent('click', stepActionName(direction), {
        thread_no: no,
        from_thread_no: step.current,
        index: order.indexOf(no),
        origin,
      });
    }
    navigate(itemPathFromWorkspace(no), { state: { ...(state as object), from: origin, order } });
  };
  return (
    <div className={styles.topBar}>
      <Link to={`${ORIGIN_PATH[origin]}${search}`} className={styles.back} data-door-back>
        ← {ORIGIN_LABEL[origin]}
      </Link>
      {/* Previous / next within the list it was opened from (case round,
          finding 6): no trip back to the list between items. */}
      {step && <ItemNav step={step} listLabel={ORIGIN_LABEL[origin]} onStep={go} />}
      {/* The number and title sit right below in the item's own header, so
          the bar repeated them (tabs round, finding 4). Only the narrow
          chat-only view, where that header is hidden, still names the item. */}
      <span className={styles.crumb} data-testid="workspace-crumb">
        {view === 'chat' ? crumb : null}
      </span>
      {/* Narrow screens stack the halves; this picks which one shows. Not
          "Ask Merlin": that is the bar's general chat, this is the item's
          own conversation (kanban 445962e2). */}
      <div className={styles.viewToggle} role="tablist" aria-label="Workspace view">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'detail'}
          className={`${styles.viewBtn} ${view === 'detail' ? styles.viewBtnOn : ''}`}
          onClick={() => onView('detail')}
        >
          Details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'chat'}
          className={`${styles.viewBtn} ${view === 'chat' ? styles.viewBtnOn : ''}`}
          onClick={() => onView('chat')}
        >
          Merlin chat
        </button>
      </div>
    </div>
  );
}

/** A link to `…#case-asks` (the Cases tab's "Waiting on people" list) lands
 *  on the asks once they have loaded; the pane scrolls itself, so the
 *  browser's own hash jump does not reach it. */
function useScrollToAsksOnHash(askCount: number) {
  const { hash } = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || hash !== '#case-asks' || askCount === 0) return;
    done.current = true;
    window.setTimeout(
      () => document.getElementById('case-asks')?.scrollIntoView({ block: 'start' }),
      300,
    );
  }, [hash, askCount]);
}

/**
 * One POST per open, even when React mounts the effect twice (StrictMode in
 * dev) or two renders race: the in-flight call is shared per thread and
 * forgotten once it settles, so the next visit records its own open.
 */
const openInFlight = new Map<string, Promise<DoorThreadSeenRead>>();
function recordOpenOnce(threadId: string): Promise<DoorThreadSeenRead> {
  const pending = openInFlight.get(threadId);
  if (pending) return pending;
  const call = recordThreadView(threadId).finally(() => {
    openInFlight.delete(threadId);
  });
  openInFlight.set(threadId, call);
  return call;
}

function ThreadWorkspace({ thread }: { thread: DoorThreadRead }) {
  // A viewer's open is never recorded as the desk's "seen" (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const { invState, userName, bumpSeen, openAnswer, followups } = useQualityTriage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dataRequests = useDoorAsks();
  const [view, setView] = useState<NarrowView>('detail');
  // Capture the seen-mark BEFORE this visit marks the thread read: the NEW
  // markers on the evidence compare against it. Keyed per thread so a
  // split-lineage jump re-captures for the next thread.
  const [seenBefore, setSeenBefore] = useState<{ id: string; mark: string | null }>(() => ({
    id: thread.id,
    mark: seenSnapshot()[thread.id] ?? null,
  }));
  if (seenBefore.id !== thread.id) {
    setSeenBefore({ id: thread.id, mark: seenSnapshot()[thread.id] ?? null });
  }
  // Reading a thread marks it read at its current revision, so the shell's
  // "updated" badge means "Merlin moved this since you looked".
  useEffect(() => {
    markThreadSeen(thread.id, thread.updated_at);
    bumpSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bumpSeen is a stable shell callback
  }, [thread.id, thread.updated_at]);
  // The server-side mark (card 10a54f95): one row per open, and the answer
  // says when THIS viewer last opened the item on any browser. Captured per
  // thread, once per visit, so the line does not move while the page is
  // open. Undefined until it lands (or when it fails): no line.
  const [lastSeen, setLastSeen] = useState<{ id: string; at: string | null | undefined }>({
    id: thread.id,
    at: undefined,
  });
  useEffect(() => {
    let cancelled = false;
    setLastSeen({ id: thread.id, at: undefined });
    (readOnly ? Promise.resolve(null) : recordOpenOnce(thread.id))
      .then((seen) => {
        if (!cancelled && seen) setLastSeen({ id: thread.id, at: seen.previous_seen_at });
      })
      .catch(() => {
        /* the mark is a convenience: without it the brief reads as before */
      });
    return () => {
      cancelled = true;
    };
  }, [thread.id, readOnly]);

  const caseFile = caseForThread(invState.investigations, thread);
  useRefreshAsksOnStageChange(caseFile?.status ?? null, dataRequests.refresh);
  const asks = useMemo(
    () =>
      caseFile
        ? asksForCase(dataRequests.requests, caseFile, thread)
        : asksForThread(dataRequests.requests, thread),
    [dataRequests.requests, caseFile, thread],
  );
  useScrollToAsksOnHash(asks.length);
  // What Merlin promised to come back on for THIS item (kanban 43160d8b).
  const itemFollowups = useMemo(
    () => (followups?.available ? followupsForThread(followups.rows, thread) : []),
    [followups?.available, followups?.rows, thread],
  );
  const cameFrom = originFromState(useLocation().state);
  const origin = cameFrom ?? threadOrigin(thread, isRoutedToUser(thread, userName));
  const ownedByViewer =
    !!thread.conversation_id && thread.conversation_owned_by_viewer !== false;
  // One number per case (proposal 6): INV once it is a case, QT before.
  const itemLabel = primaryRef(thread, caseFile).label;
  const chatDrafts = useMemo(() => splitAsks(asks, { hideStepDrafts: true }).drafts, [asks]);
  // "Ask <desk>" on a case item — from the case file, its pop-out, or a
  // never-asked row in the panel (proposal 2): one modal for all three.
  const [askTask, setAskTask] = useState<DoorCaseTaskRead | null>(null);
  const showPanel = !!dataRequests.available;
  const scope = { thread_no: thread.thread_no, investigation_no: caseFile?.investigation_no ?? null };

  return (
    <div className={styles.workspace} data-view={view}>
      <BackBar
        origin={origin}
        crumb={`${itemLabel} · ${thread.title}`}
        view={view}
        onView={setView}
        itemNos={[thread.thread_no, ...(caseFile ? [caseFile.investigation_no] : [])]}
      />
      <div className={`${styles.panes} ${ownedByViewer || showPanel ? '' : styles.panesLocked}`}>
        <ThreadDetailPane
          key={thread.id}
          thread={thread}
          threads={invState.threads}
          caseFile={caseFile}
          seenBefore={seenBefore.mark}
          lastSeenAt={lastSeen.id === thread.id ? lastSeen.at : undefined}
          asks={dataRequests.available ? asks : []}
          onAskUpdated={dataRequests.applyUpdate}
          onThreadUpdated={invState.applyThreadUpdate}
          onInvestigationOpened={(updated, investigation) => {
            invState.applyThreadUpdate(updated);
            invState.applyUpdate(investigation);
          }}
          onInvestigationUpdated={invState.applyUpdate}
          onJumpToThread={(target) => navigate(threadWorkspacePath(target, 'tab'))}
          viewerName={userName}
          onAskSent={dataRequests.refresh}
          onAskTask={setAskTask}
          followups={itemFollowups}
        />
        <div className={styles.rail}>
          {showPanel && (
            <AskedOnCasePanel
              asks={asks}
              scope={scope}
              onUpdated={dataRequests.applyUpdate}
              onRead={openAnswer ?? noop}
              handOff={handOffRow(thread, userName)}
            />
          )}
          {ownedByViewer ? (
            <ConversationPane
              key={thread.conversation_id ?? `none-${thread.id}`}
              conversationId={thread.conversation_id}
              ownedByViewer={ownedByViewer}
              ownerLabel={thread.routed_to}
              userId={user?.id ?? null}
              isAdmin={user?.role === 'ADMIN'}
              hideSystemSeeded
              itemNo={itemLabel}
              noConversationCopy="Merlin hasn’t opened a conversation for this thread yet."
              asks={showPanel ? asks : undefined}
              foldedByDefault={showPanel ? railHasRows(asks) : undefined}
              trackContext={scope}
            />
          ) : (
            /* Nothing the viewer can do in a locked chat pane, so it no longer
               takes 40% of the screen: one line saying whose it is and where
               to ask instead. */
            <aside className={styles.chatLocked} data-testid="chat-locked-note">
              <strong>Merlin’s conversation on this item</strong>
              <span>
                {thread.conversation_id
                  ? `It belongs to ${thread.routed_to ?? 'the person it was sent to'}, so only they can read it or continue it here.`
                  : 'Merlin hasn’t opened a conversation for this item yet.'}{' '}
                To ask Merlin about it yourself, use <Link to="../ask">Ask Merlin</Link> at the top
                and mention {itemLabel}.
              </span>
            </aside>
          )}
        </div>
      </div>
      {askTask && caseFile && (
        <TaskAskModal
          investigation={caseFile}
          task={askTask}
          chatDrafts={chatDrafts}
          thread={thread}
          asks={asks}
          viewerName={userName}
          onUpdated={dataRequests.applyUpdate}
          onRefresh={dataRequests.refresh}
          onClose={() => setAskTask(null)}
        />
      )}
    </div>
  );
}

function CaseWorkspace({ investigation }: { investigation: DoorInvestigationRead }) {
  const { invState, userName, openAnswer } = useQualityTriage();
  const { user } = useAuth();
  const dataRequests = useDoorAsks();
  const onReadAnswer = openAnswer ?? noop;
  const [view, setView] = useState<NarrowView>('detail');
  // "Ask <desk>" on one of the case's outstanding items (kanban 52a4147a): a
  // chat-born case has no thread, so the ask is sent through the case.
  const [askTask, setAskTask] = useState<DoorCaseTaskRead | null>(null);
  const { popped, open: popOut, close: closePopout } = useCaseFilePopout();
  const asks = useMemo(
    () => asksForCase(dataRequests.requests, investigation, null),
    [dataRequests.requests, investigation],
  );
  const chatDrafts = useMemo(() => splitAsks(asks, { hideStepDrafts: true }).drafts, [asks]);
  useScrollToAsksOnHash(asks.length);
  useRefreshAsksOnStageChange(investigation.status, dataRequests.refresh);
  // The pane shows the originating conversation first; "Open" on one of the
  // case file's linked-conversation rows switches it (kanban b32f10ce).
  const [conversationId, setConversationId] = useState<string | null>(
    () => investigation.originating_conversation_id ?? investigation.conversation_ids[0] ?? null,
  );
  const showConversation = (id: string) => {
    setConversationId(id);
    setView('chat');
  };
  const cameFrom = originFromState(useLocation().state);

  return (
    <div className={styles.workspace} data-view={view}>
      <BackBar
        origin={cameFrom ?? 'cases'}
        crumb={`${investigation.investigation_no} · ${investigation.title}`}
        view={view}
        onView={setView}
        itemNos={[investigation.investigation_no]}
      />
      <div className={styles.panes}>
        <div className={styles.detail}>
          <CaseFileSection
            investigation={investigation}
            onUpdated={invState.applyUpdate}
            onPopOut={popOut}
            openAskCount={
              dataRequests.available
                ? asks.filter((r) => r.status === 'open').length
                : undefined
            }
            activeConversationId={conversationId}
            onOpenLinkedConversation={showConversation}
            asks={dataRequests.available ? asks : undefined}
            onAskTask={dataRequests.available ? setAskTask : undefined}
          />
          {dataRequests.available && (
            <CaseAsksHost
              title="Asks on this case"
              requests={asks}
              onRequestUpdated={dataRequests.applyUpdate}
              trackContext={{ investigation_no: investigation.investigation_no }}
            />
          )}
        </div>
        <div className={styles.rail}>
          {dataRequests.available && (
            <AskedOnCasePanel
              asks={asks}
              scope={{ investigation_no: investigation.investigation_no }}
              onUpdated={dataRequests.applyUpdate}
              onRead={onReadAnswer}
            />
          )}
          <ConversationPane
            key={conversationId ?? `none-${investigation.id}`}
            conversationId={conversationId}
            ownedByViewer
            ownerLabel={null}
            userId={user?.id ?? null}
            isAdmin={user?.role === 'ADMIN'}
            itemNo={investigation.investigation_no}
            noConversationCopy="This case has no Merlin conversation yet."
            asks={dataRequests.available ? asks : undefined}
            foldedByDefault={dataRequests.available ? railHasRows(asks) : undefined}
            trackContext={{ investigation_no: investigation.investigation_no }}
          />
        </div>
      </div>
      {popped && (
        <CaseFileModal
          investigation={investigation}
          onClose={closePopout}
          onUpdated={invState.applyUpdate}
          activeConversationId={conversationId}
          onOpenLinkedConversation={(id) => {
            closePopout();
            showConversation(id);
          }}
          asks={dataRequests.available ? asks : undefined}
          onAskTask={dataRequests.available ? setAskTask : undefined}
        />
      )}
      {askTask && (
        <TaskAskModal
          key={`task-${askTask.id}`}
          investigation={investigation}
          task={askTask}
          chatDrafts={chatDrafts}
          viewerName={userName}
          onUpdated={dataRequests.applyUpdate}
          onRefresh={dataRequests.refresh}
          onClose={() => setAskTask(null)}
        />
      )}
    </div>
  );
}

function NotFound({ kind, reference }: { kind: 'thread' | 'case'; reference: string }) {
  const { invState } = useQualityTriage();
  return (
    <div className={styles.workspace}>
      <div className={styles.topBar}>
        <Link to={kind === 'case' ? '../cases' : '..'} className={styles.back}>
          ← {kind === 'case' ? 'Cases' : 'Triage'}
        </Link>
      </div>
      <p className={styles.notFound} role="status">
        {/* The threads list lands seconds after the investigations on a real
            backend: a deep link used to flash "not tracked" until it did. */}
        {invState.threadsLoaded === false ||
        (invState.threads.length === 0 && invState.investigations.length === 0)
          ? 'Loading…'
          : `No ${kind} called ${reference} is tracked on this door. It may have been removed, or the link is from another deploy.`}
      </p>
    </div>
  );
}

/** `threads/:threadRef` — the thread's workspace (its case file rides along). */
export function ThreadWorkspacePage() {
  const { threadRef } = useParams<{ threadRef: string }>();
  const { invState } = useQualityTriage();
  // The recorder's leave/return markers name the thread open at that moment.
  useRecorderObject({ thread_ref: threadRef });
  const thread = resolveThread(invState.threads, threadRef);
  if (!thread) return <NotFound kind="thread" reference={threadRef ?? ''} />;
  return <ThreadWorkspace thread={thread} />;
}

/** `cases/:caseRef` — a case opened from a thread lands on that thread's
 *  workspace; a chat-born case gets the case-only workspace. */
export function CaseWorkspacePage() {
  const { caseRef } = useParams<{ caseRef: string }>();
  const { invState } = useQualityTriage();
  const investigation = resolveCase(invState.investigations, caseRef);
  if (!investigation) return <NotFound kind="case" reference={caseRef ?? ''} />;
  const thread = threadForCase(invState.threads, investigation);
  if (thread) return <ThreadWorkspace thread={thread} />;
  return <CaseWorkspace investigation={investigation} />;
}
